<script>
import axios from 'axios';
import truncate from 'truncate';
import format from 'string-format';
import Paginate from 'vuejs-paginate-next';
import { escapeHtml, decodeHtml } from '../utils/htmlUtils';

export default {
    props: {
        s: String,
        labels: String,
        tabs: String,
        showTabs: Boolean,
        lang: String,
    },
    computed: {
        getResultLabel() {
            if (this.axiosParams.subtype === '') {
                return format(this.parsedLabels.search_result_all, escapeHtml(this.searchWord), this.totalResult);
            } else {
                const tab = this.parsedTabs.find((item) => item.subtype == this.axiosParams.subtype);
                return format(this.parsedLabels.search_result, escapeHtml(this.searchWord), tab.count, escapeHtml(tab.title.toLowerCase()));
            }
        }
    },
    mounted() {
        this.axiosParams.search = this.s;
        this.parsedLabels = JSON.parse(this.labels);
        this.parsedTabs = JSON.parse(this.tabs);
        this.axiosParams.lang = this.lang;
        this.getSearch();
    },
    data() {
        return {
            axiosUrl: '/wp-json/wp/v2/search',
            axiosParams: {
                per_page: 6,
                page: 1,
                search: '',
                subtype: '',
                _embed: 'self',
                lang: '',
            },
            previousSearch: '',
            result: [],
            pageCount: 1,
            totalResult: 0,
            searchWord: '',
            parsedLabels: { search_result_all: '', search_result: '' },
            parsedTabs: [],
            loading: false,
        };
    },
    components: {
        paginate: Paginate,
    },
    watch: {
        'axiosParams.page'(newValue, oldValue) {
            if (newValue != oldValue) {
                this.getSearch();
            }
        },
        'axiosParams.subtype'(newValue, oldValue) {
            if (newValue != oldValue) {
                this.getSearch();
            }
        },
    },
    methods: {
        decodeHtml,
        getSearch() {
            if (this.axiosParams.search === '') {
                return;
            }

            this.loading = true;

            let params = Object.assign({}, this.axiosParams);
            if (params.subtype === '') {
                delete params.subtype;
            }

            axios
                .get(this.axiosUrl, { params: { search: params.search, per_page: 9999, lang: params.lang } })
                .then((response) => {
                    this.setTotalResult(response.headers);
                    this.updateTabs(response.data);
                    this.previousSearch = this.axiosParams.search;
                })
                .catch((error) => {
                    console.log(error);
                });

            axios
                .get(this.axiosUrl, { params: params })
                .then((response) => {
                    this.searchWord = this.axiosParams.search;
                    this.result = response.data;
                    this.configPagination(response.headers);
                    setTimeout(() => {
                        this.loading = false;
                    }, 1000);
                })
        },
        truncateString(string, limit) {
            return truncate(string, limit);
        },
        doPagination(page) {
            this.axiosParams.page = page;
        },
        formSubmit(e) {
            e.preventDefault();
            this.resetAxiosParams();
            this.getSearch();
        },
        configPagination(headers) {
            this.pageCount = +headers['x-wp-totalpages'];
        },
        setTotalResult(headers) {
            this.totalResult = +headers['x-wp-total'];
        },
        resetAxiosParams() {
            this.axiosParams.page = 1;
        },
        updateTabs(data) {
            return this.parsedTabs.map((tab) => {
                tab.count = data.filter((item) => item.subtype === tab.subtype).length;
            });
        },
        setFilter(subtype) {
            this.axiosParams.subtype = subtype;
            this.resetAxiosParams();
        },
        getCategoryTag(item) {
            if (!item.category || !item.category.name) {
                return;
            }

            return `<a href="${escapeHtml(item.archive_url)}?filter=${escapeHtml(item.category.slug)}">${escapeHtml(item.category.name)}</a>`;
        },
        getPosttype(item) {
            if (!item.subtype || item.subtype === 'page') {
                return;
            }

            const tab = this.parsedTabs.find((tab) => tab.subtype == item.subtype);

            return tab ? tab.title : null;
        },
    },
};
</script>

<template>
    <h1><span class="sr-only">{{parsedLabels.title}}</span></h1>
    <form class="form" @submit="formSubmit">
        <fieldset>
            <input class="form__field" type="text" v-model="axiosParams.search" :aria-label="parsedLabels.search_label" required />
            <button type="submit" class="button button--search" :aria-label="parsedLabels.search_button"></button>
        </fieldset>
    </form>
    <div class="search-result">
        <div class="search-result__tabs-container">
            <ul class="search-result__tabs" v-if="showTabs">
                <li class="search-result__tab"
                    v-bind:class="{ 'search-result__tab--active': axiosParams.subtype == '', 'search-result__tab--disabled': totalResult == 0 }"
                    @click="setFilter('')">{{ parsedLabels.default_tab }} ({{ totalResult }})</li>
                <li class="search-result__tab"
                    v-bind:class="[{ 'search-result__tab--active': axiosParams.subtype == item.subtype, 'search-result__tab--disabled': item.count == 0}, `search-result__tab--${item.title}` ]"
                    v-for="item in parsedTabs" :key="item.subtype" @click="setFilter(item.subtype)">{{ item.title }} ({{
                        item.count ?? 0
                    }})</li>
            </ul>
        </div>
        <p class="search-result__result-label" v-html="getResultLabel"></p>
        <div v-if="loading" class="search-result__loader">
            <div class="loader"></div>
        </div>
        <div class="search-result__content" v-if="!loading">
            <ul class="search-result__list">
                <li class="search-result__item" :class="[`search-result__item--${item.subtype}`]" v-for="item in result" :key="item.id">
                    <div v-if="item._embedded && item._embedded.self[0].image" class="search-result__item-image-container">
                        <img v-bind:src="item._embedded.self[0].image" />
                    </div>
                    <div class="search-result__item-content">
                        <a :href="item.url">
                            <h2 class="search-result__item-title">{{ decodeHtml(item.title) }}</h2>
                        </a>
                        <p v-if="item.subtype == 'contact'" class="search-result__item-text">{{ item._embedded.self[0].position }}</p>
                        <p v-else-if="item._embedded" class="search-result__item-text">{{ decodeHtml(item._embedded.self[0].excerpt_trimmed) }}</p>
                    </div>
                    <div class="search-result__item-meta">
                        <span class="search-result__item-category">{{ getPosttype(item) }}</span>
                    </div>
                </li>
            </ul>
            <p v-if="result.length == 0">{{ parsedLabels.no_result }}</p>
        </div>
        <paginate v-if="pageCount > 1" v-model="axiosParams.page" :page-count="pageCount" :page-range="3"
            :margin-pages="1" :click-handler="doPagination" :prev-text="''" :next-text="''"
            :container-class="'pagination'" :page-class="'pagination__item'" :page-link-class="'pagination__item-link'"
            :next-class="'button button--next'" :prev-class="'button button--prev'"
            :disabled-class="'button--disabled'"></paginate>
    </div>
</template>

<style lang="scss" scoped>
.form {
    @include wrap(content);

    margin-top: 80px;
    margin-bottom: 32px;

    @include media(M) {
        margin-bottom: 64px;
    }

    fieldset {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
    }

    input {
        width: 100%;
    }
}
</style>
